/**
 * PlanningLLMService - Manages LLM interactions for presentation planning
 * This service communicates with a backend API to generate and modify presentation plans
 */
export class PlanningLLMService {
    constructor() {
        this.currentPlan = null;
        this.environment = null;
    }
    static instance() {
        if (!PlanningLLMService.instanceRef) {
            PlanningLLMService.instanceRef = new PlanningLLMService();
        }
        return PlanningLLMService.instanceRef;
    }
    /**
     * Generate initial presentation plan
     */
    async generateInitialPlan(payload, handlers) {
        if (!this.environment?.backendBaseUrl) {
            const error = new Error('Backend URL not configured. Call setEnvironment() first.');
            console.error('[PlanningLLMService]', error.message);
            handlers?.onError?.(error);
            return;
        }
        try {
            console.log('[PlanningLLMService] Generating initial plan:', payload);
            // Make request to backend
            const response = await fetch(`${this.environment.backendBaseUrl}/api/planning/generate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...payload,
                    documentId: this.environment.currentdocumentId,
                }),
            });
            if (!response.ok) {
                throw new Error(`Backend returned ${response.status}: ${response.statusText}`);
            }
            const data = await response.json();
            if (data.plan) {
                this.currentPlan = data.plan;
                handlers?.onPlanCreated?.(data.plan);
            }
            handlers?.onComplete?.();
        }
        catch (error) {
            console.error('[PlanningLLMService] Error generating plan:', error);
            handlers?.onError?.(error);
        }
    }
    /**
     * Send chat message to modify the plan
     */
    async sendChatMessage(message, handlers) {
        if (!this.environment?.backendBaseUrl) {
            const error = new Error('Backend URL not configured. Call setEnvironment() first.');
            console.error('[PlanningLLMService]', error.message);
            handlers?.onError?.(error);
            return;
        }
        if (!this.currentPlan) {
            const error = new Error('No current plan. Generate a plan first.');
            console.error('[PlanningLLMService]', error.message);
            handlers?.onError?.(error);
            return;
        }
        try {
            console.log('[PlanningLLMService] Sending chat message:', message);
            // Make request to backend
            const response = await fetch(`${this.environment.backendBaseUrl}/api/planning/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message,
                    plan: this.currentPlan,
                    documentId: this.environment.currentdocumentId,
                }),
            });
            if (!response.ok) {
                throw new Error(`Backend returned ${response.status}: ${response.statusText}`);
            }
            const data = await response.json();
            if (data.plan) {
                this.currentPlan = data.plan;
                handlers?.onPlanUpdated?.(data.plan);
            }
            if (data.textChunk) {
                handlers?.onTextChunk?.(data.textChunk);
            }
            handlers?.onComplete?.();
        }
        catch (error) {
            console.error('[PlanningLLMService] Error sending message:', error);
            handlers?.onError?.(error);
        }
    }
    /**
     * Set the current plan (useful after modifications)
     */
    setPlan(plan) {
        console.log('[PlanningLLMService] Setting plan:', plan.id);
        this.currentPlan = plan;
    }
    /**
     * Get the current plan
     */
    getPlan() {
        return this.currentPlan;
    }
    /**
     * Configure the service environment
     */
    setEnvironment(env) {
        console.log('[PlanningLLMService] Setting environment:', {
            backendBaseUrl: env.backendBaseUrl,
            adapter: env.adapter,
            hasdocumentId: !!env.currentdocumentId,
        });
        this.environment = env;
    }
    /**
     * Get the current environment
     */
    getEnvironment() {
        return this.environment;
    }
    /**
     * Reset the service state
     */
    reset() {
        console.log('[PlanningLLMService] Resetting service');
        this.currentPlan = null;
        this.environment = null;
    }
}
PlanningLLMService.instanceRef = null;
export default PlanningLLMService;
